import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useSuiteStore } from '@/stores/useSuiteStore'
import { withApiState } from '@/utils/withApiState'

type FeatureType = 'PAGE' | 'CAPABILITY' | 'UMBRELLA'
export type FeatureStatus = 'ACTIVE' | 'INACTIVE' | 'DEPRECATED'
type PageMatchType = 'PATH' | 'PATH_AND_QUERY' | 'NAMED_ROUTE'

export interface FeatureAdmin {
  uuid: string
  key: string
  type: FeatureType
  label: string
  category: string | null
  status: FeatureStatus
  orphan: boolean
  requiredPermissions: string[]
  serviceTypes: string[]
  capabilityEndpoint: string | null
  pagesCount: number
  createdAt: string
  updatedAt: string
}

export interface PageAdmin {
  uuid: string
  key: string
  label: string
  routePattern: string
  matchType: PageMatchType
  matchValue: string
  orphan: boolean
  requiredPermissions: string[]
  featureUuid: string
  featureKey: string | null
  createdAt: string
  updatedAt: string
}

export interface FeatureDetail extends FeatureAdmin {
  pages: PageAdmin[]
}

interface SyncDiffEntry {
  key: string
  label?: string | null
}

interface SyncDiffScope {
  created: SyncDiffEntry[]
  updated: SyncDiffEntry[]
  orphaned: SyncDiffEntry[]
  reactivated: SyncDiffEntry[]
}

export interface SyncResult {
  dryRun: boolean
  source?: string
  version?: string
  executedAt: string
  features: SyncDiffScope
  pages: SyncDiffScope
}

export interface UpdateFeaturePayload {
  label?: string
  category?: string | null
  status?: FeatureStatus
}

export interface UpdatePagePayload {
  label?: string
}

type StoreResult<T> = { ok: true; data: T } | { ok: false; message: string }

async function readError(res: Response, fallback: string): Promise<string> {
  try {
    const body = await res.json()
    if (body?.message) return Array.isArray(body.message) ? body.message.join(', ') : String(body.message)
  } catch {
    // ignore
  }
  return fallback
}

export const useFeatureRegistryAdminStore = defineStore('featureRegistryAdmin', () => {
  const suiteStore = useSuiteStore()
  const features = ref<FeatureAdmin[]>([])
  const pages = ref<PageAdmin[]>([])
  const isLoadingFeatures = ref(false)
  const isLoadingPages = ref(false)
  const lastSyncResult = ref<SyncResult | null>(null)

  async function fetchFeatures(): Promise<FeatureAdmin[]> {
    const result = await withApiState(
      async () => {
        const res = await suiteStore.suiteFetch('client-space/admin/feature-registry/features', {
          method: 'GET',
          credentials: 'include',
        })
        if (res && typeof res !== 'boolean' && res.ok) {
          features.value = (await res.json()) as FeatureAdmin[]
          return features.value
        }
        return undefined
      },
      { loading: isLoadingFeatures, logLabel: 'Erreur récupération features' },
    )
    return result ?? []
  }

  async function fetchFeatureDetail(uuid: string): Promise<FeatureDetail | null> {
    const res = await suiteStore.suiteFetch(`client-space/admin/feature-registry/features/${uuid}`, {
      method: 'GET',
      credentials: 'include',
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      return (await res.json()) as FeatureDetail
    }
    return null
  }

  async function updateFeature(uuid: string, payload: UpdateFeaturePayload): Promise<StoreResult<FeatureAdmin>> {
    const res = await suiteStore.suiteFetch(`client-space/admin/feature-registry/features/${uuid}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      const data = (await res.json()) as FeatureAdmin
      const idx = features.value.findIndex((f) => f.uuid === uuid)
      if (idx >= 0) features.value[idx] = data
      return { ok: true, data }
    }
    const message = res && typeof res !== 'boolean'
      ? await readError(res, 'Erreur lors de la mise à jour')
      : 'Erreur lors de la mise à jour'
    return { ok: false, message }
  }

  async function fetchPages(): Promise<PageAdmin[]> {
    const result = await withApiState(
      async () => {
        const res = await suiteStore.suiteFetch('client-space/admin/feature-registry/pages', {
          method: 'GET',
          credentials: 'include',
        })
        if (res && typeof res !== 'boolean' && res.ok) {
          pages.value = (await res.json()) as PageAdmin[]
          return pages.value
        }
        return undefined
      },
      { loading: isLoadingPages, logLabel: 'Erreur récupération pages' },
    )
    return result ?? []
  }

  async function updatePage(uuid: string, payload: UpdatePagePayload): Promise<StoreResult<PageAdmin>> {
    const res = await suiteStore.suiteFetch(`client-space/admin/feature-registry/pages/${uuid}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      const data = (await res.json()) as PageAdmin
      const idx = pages.value.findIndex((p) => p.uuid === uuid)
      if (idx >= 0) pages.value[idx] = data
      return { ok: true, data }
    }
    const message = res && typeof res !== 'boolean'
      ? await readError(res, 'Erreur lors de la mise à jour')
      : 'Erreur lors de la mise à jour'
    return { ok: false, message }
  }

  async function runSync(manifest: object, dryRun: boolean): Promise<StoreResult<SyncResult>> {
    const res = await suiteStore.suiteFetch('client-space/admin/feature-registry/sync', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...manifest, dryRun }),
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      const data = (await res.json()) as SyncResult
      lastSyncResult.value = data
      // Si pas dryRun, recharger les listes
      if (!dryRun) {
        await Promise.all([fetchFeatures(), fetchPages()])
      }
      return { ok: true, data }
    }
    const message = res && typeof res !== 'boolean'
      ? await readError(res, 'Erreur lors du sync')
      : 'Erreur lors du sync'
    return { ok: false, message }
  }

  async function autoSync(dryRun: boolean): Promise<StoreResult<SyncResult>> {
    const res = await suiteStore.suiteFetch('client-space/admin/feature-registry/auto-sync', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ dryRun }),
    })
    if (res && typeof res !== 'boolean' && res.ok) {
      const data = (await res.json()) as SyncResult
      lastSyncResult.value = data
      if (!dryRun) {
        await Promise.all([fetchFeatures(), fetchPages()])
      }
      return { ok: true, data }
    }
    const message = res && typeof res !== 'boolean'
      ? await readError(res, "Erreur lors de l'auto-sync")
      : "Erreur lors de l'auto-sync"
    return { ok: false, message }
  }

  return {
    features,
    pages,
    isLoadingFeatures,
    isLoadingPages,
    lastSyncResult,
    fetchFeatures,
    fetchFeatureDetail,
    updateFeature,
    fetchPages,
    updatePage,
    runSync,
    autoSync,
  }
})
